import { TitleCasePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { CommodityViewModel } from '@models/commodities.class';
import { CURRENCY_TYPE_ID } from '@models/commodities.constant';
import { MarketViewModel } from '@models/market.class';
import { Store } from '@ngrx/store';
import { AppManagementService } from '@services/appManagement.service';
import { ContractManagementService } from '@services/contractManagement.service';
import { MarketService } from '@services/market.service';
import { Map as ImmutableMap } from 'immutable';
import { isEmpty } from 'lodash';
import {
  EMPTY,
  empty as observableEmpty,
  forkJoin as observableForkJoin,
  interval as observableInterval,
  merge as observableMerge,
  Observable,
  Subscription,
  throwError as observableThrowError,
  of,
} from 'rxjs';
import {
  catchError,
  distinct,
  filter,
  map,
  mergeMap,
  take,
  tap,
  toArray,
} from 'rxjs/operators';
import { Configurations } from 'src/app/app.configurations';
import { AppConfiguration } from 'src/app/appConfiguration.class';
import { AppState } from 'src/app/state/app-state';
import {
  SOLACE_INITIALIZER,
  SOLACE_TOPICS,
  USA_MARKET_INITIALIZER,
} from 'src/app/state/app.reducer';
import { selectIsUsa } from 'src/app/state/selectors/current-contract.selectors';
import { selectFeatureFlag } from 'src/app/state/selectors/feature-flag.selectors';
import { NotificationService } from 'src/core/services/notification.service';
import { SearchService } from 'src/core/services/search.service';
import { SessionService } from 'src/core/services/session.service';
import {
  CommodityGroups,
  MarketType,
  MarketTypeString,
  SharedSolaceMainService,
  SolacePropertie,
} from 'src/solace';

@Injectable()
export class SolaceProxyService {
  private initialized = false;

  private solaceTopics: any[];
  private nacData: MarketViewModel[];
  private sicData: MarketViewModel[];
  private usaData: MarketViewModel[];
  private indexesData: CommodityViewModel[];
  private currenciesData: CommodityViewModel[];
  private commoditiesData: CommodityViewModel[];
  private isInitializedRollout: boolean;

  private intervalTimeOut: number = 5 * 60 * 1000;

  private intervalMarketDataObservable$: Observable<any>;

  private intervalMarketDataSubscription: Subscription;
  private changeContractSubscription: Subscription;
  private rolloutSubscription: Subscription;
  private loggedInSubscription: Subscription;
  private isUsaContract = false;
  private isContractsBPInitialized: boolean;
  private isElegible = false;

  constructor(
    private store: Store<AppState>,
    private sharedSolaceMainService: SharedSolaceMainService,
    private appManagementRestService: AppManagementService,
    private notificationService: NotificationService,
    private searchService: SearchService,
    private configurations: Configurations,
    private marketService: MarketService,
    private sessionService: SessionService,
    private contractsManagement: ContractManagementService,
  ) {}

  init(): void {
    this.scanIsUsaContract();
    this.scanForRollout();

    const WAIT_TIME = 80;
    if (this.isInitializedRollout && this.isContractsBPInitialized) {
      this._init()
        .pipe(take(1))
        .subscribe(
          () => {
            this.listenUserIsElegible();
          },
          () => {
            // just catch error
          },
        );
    } else {
      setTimeout(() => {
        this.init();
      }, WAIT_TIME);
    }
  }

  listenUserIsElegible(): void {
    this.store
      .select('currentContract')
      .pipe(
        map((values: any) => values.get('contractID')),
        filter((contractId: any) => contractId !== undefined),
        distinct(),
        mergeMap((contractId) =>
          this.contractsManagement.getContractBuyingPower(contractId),
        ),
        mergeMap((buyingPower) => {
          return this.store.select(selectFeatureFlag('realTimeMinAmount')).pipe(
            map(Number),
            mergeMap((minAmount) => {
              return of(buyingPower.marketValueTotal >= minAmount);
            }),
          );
        }),
      )
      .subscribe((elegible) => {
        if (
          (elegible || this.isElegible) &&
          !this.sharedSolaceMainService.isConnected
        ) {
          this.connect();
        }
        this.isElegible = elegible;
      });
  }

  _init(): Observable<any> {
    const appConfiguration: AppConfiguration =
      this.configurations.appConfiguration;
    const solaceConfig: any = {
      url: appConfiguration.solace.url,
      vpnName: appConfiguration.solace.vpnName,
      userName: appConfiguration.solace.userName,
      password: appConfiguration.solace.password,
      topicSplitter: '/',
      connectRetryCount: +appConfiguration.solace.connectRetryCount,
      subscriptionTimeout: +appConfiguration.solace.subscriptionTimeout,
      subscriptionRetryCount: +appConfiguration.solace.subscriptionRetryCount,
      subscriptionRetryInterval:
        +appConfiguration.solace.subscriptionRetryInterval,
      userId: this.sessionService.getUserId(),
    };
    const solaceTopics$: Observable<any> = this.getSolaceTopics();
    this.initInternal();

    return observableForkJoin(
      solaceTopics$,
      this.getMarketsData(false, this.isUsaContract),
    ).pipe(
      tap(() => {
        if (
          (!this.isUsaContract &&
            this.sharedSolaceMainService.init(
              solaceConfig,
              this.solaceTopics,
              this.nacData,
              this.sicData,
              this.indexesData,
              this.commoditiesData,
              this.currenciesData,
            )) ||
          (this.isUsaContract &&
            this.sharedSolaceMainService.initUsa(
              solaceConfig,
              this.solaceTopics,
              this.usaData,
            ))
        ) {
          this.store.dispatch({
            type: SOLACE_INITIALIZER,
            payload: true,
          });
        }
      }),
      catchError((error) => {
        console.error(error);
        return EMPTY;
      }),
    );
  }

  connect(): void {
    this.sharedSolaceMainService.connect();
  }

  disconnect(): void {
    this.sharedSolaceMainService.disconnect();
  }

  getMarketsData(isTimer?: boolean, isUsaContract?: boolean): Observable<any> {
    this.intervalMarketDataSubscription &&
      this.intervalMarketDataSubscription.unsubscribe();

    return isUsaContract
      ? this.getUsaMarketData(isTimer)
      : this.getNationalMarketData(isTimer);
  }

  private getUsaMarketData(isTimer?: boolean): Observable<any> {
    this.getIndexes();

    return this.marketService.getMarket(MarketTypeString.USA).pipe(
      map((marketData: any) => {
        if (this.initialized) {
          this.sharedSolaceMainService.updateMarketData(
            MarketType.USA,
            marketData,
          );
        } else {
          this.usaData = marketData;
          this.initialized = true;
        }
        !isTimer && this.issuesToSet(marketData);

        this.intervalMarketDataSubscription =
          this.intervalMarketDataObservable$ &&
          this.intervalMarketDataObservable$.pipe(take(1)).subscribe(() => {
            this.getMarketsData(true, true)
              .pipe(
                take(1),
                catchError(() => observableEmpty()),
              )
              .subscribe();
          });
      }),
    );
  }

  private getIndexes() {
    this.getCommodity(CommodityGroups.indexes).subscribe((indexes) => {
      this.indexesData = indexes;
      this.sharedSolaceMainService.updateIndexes(indexes);
    });
  }

  private getNationalMarketData(isTimer?: boolean) {
    const nacMarket$ = this.marketService.getMarket(MarketTypeString.BMV);

    const sicMarket$ = this.marketService.getMarket(MarketTypeString.SIC);

    const indexes$ = this.getCommodity(CommodityGroups.indexes);
    const commodities$ = this.getCommodity(CommodityGroups.commodities);
    const currencies$ = this.getCommodity(CommodityGroups.currencies);

    const PESO_MXN = 'PESO MXN';
    const filteredCurrencies$ = currencies$.pipe(
      map((currencies) => {
        const currencyExceptions: any = {
          USD: {
            solaceName: PESO_MXN,
            currencyType: CURRENCY_TYPE_ID.MXN,
          },
          'Dolar USD': {
            solaceName: PESO_MXN,
            currencyType: CURRENCY_TYPE_ID.MXN,
          },
        };
        return currencies
          .filter(
            (currency) =>
              currencyExceptions[currency.name] ||
              currency.currencyType === CURRENCY_TYPE_ID.USD,
          )
          .map((currency) => {
            const hasException = currencyExceptions[currency.name];
            const name = hasException ? hasException.solaceName : currency.name;
            currency.name = name;

            return currency;
          });
      }),
    );

    return observableForkJoin(
      nacMarket$,
      sicMarket$,
      indexes$,
      commodities$,
      filteredCurrencies$,
      (nac: any, sic: any, indexes, commodities, currencies) => {
        if (this.initialized) {
          this.sharedSolaceMainService.updateData(
            nac,
            sic,
            indexes,
            commodities,
            currencies,
          );
        } else {
          this.nacData = nac;
          this.sicData = sic;
          this.indexesData = indexes;
          this.commoditiesData = commodities;
          this.currenciesData = currencies;
          this.initialized = true;
        }
        !isTimer && this.issuesToSet(nac);
        !isTimer && this.issuesToSet(sic);

        this.store.dispatch({
          type: USA_MARKET_INITIALIZER,
          payload: false,
        });
      },
    ).pipe(
      catchError((error: any) => {
        this.notificationService.showServiceError(error);

        return observableThrowError(error);
      }),
      tap(() => {
        this.intervalMarketDataSubscription =
          this.intervalMarketDataObservable$ &&
          this.intervalMarketDataObservable$.pipe(take(1)).subscribe(() => {
            this.getMarketsData(true)
              .pipe(
                take(1),
                catchError(() => observableEmpty()),
              )
              .subscribe();
          });
      }),
    );
  }

  private scanForRollout() {
    if (this.rolloutSubscription) {
      return;
    }

    this.rolloutSubscription = this.store
      .select('initRollout')
      .pipe(filter((initRollout: any) => initRollout !== undefined))
      .subscribe((initRollout: any) => {
        this.isInitializedRollout = initRollout;
      });
  }

  private scanIsUsaContract() {
    if (this.changeContractSubscription) {
      return;
    }

    this.changeContractSubscription = this.store
      .select(selectIsUsa)
      .pipe(filter((isUsa: any) => isUsa !== undefined))
      .subscribe((isUsa: boolean) => {
        this.isContractsBPInitialized = true;
        this.cleanSearchService();
        this.getMarketsData(false, isUsa)
          .pipe(
            take(1),
            catchError(() => observableEmpty()),
          )
          .subscribe();

        this.isUsaContract = isUsa;
      });
  }

  private initInternal(): void {
    this.loggedInSubscription = this.store
      .select('loggedIn')
      .subscribe((value: boolean) => {
        if (value) {
          this.intervalTimeOut =
            +this.configurations.appConfiguration.timeOuts.timeOut5Min;
          this.intervalMarketDataObservable$ = observableInterval(
            this.intervalTimeOut,
          );
        } else {
          this.sharedSolaceMainService.disconnect();
          if (this.intervalMarketDataSubscription) {
            this.intervalMarketDataSubscription.unsubscribe();
          }
        }

        this.nacData = [];
        this.sicData = [];
        this.indexesData = [];
        this.usaData = [];

        this.initialized = false;
      });
  }

  private getCommodity(
    commodityId: CommodityGroups,
  ): Observable<CommodityViewModel[]> {
    return this.marketService.getCommodity(commodityId).pipe(
      map((commodities: any) =>
        !isEmpty(commodities)
          ? commodities.map((item: any) => CommodityViewModel.fromJSON(item))
          : [],
      ),
      catchError((error: any) => {
        this.notificationService.showServiceError(error);

        return observableThrowError(error);
      }),
    );
  }

  private getSolaceTopics(): Observable<any> {
    let userHash = '';
    const solaceTopics$ = this.store.select('loggedInUser').pipe(
      filter((mapShadowed: any) => ImmutableMap.isMap(mapShadowed)),
      filter(
        (mapShadowed: any) =>
          mapShadowed.get('hasLevel2') !== undefined &&
          mapShadowed.get('hash') !== undefined,
      ),
      map((mapShadowed: any) => {
        return {
          hasLevel2: mapShadowed.get('hasLevel2'),
          hash: mapShadowed.get('hash'),
        };
      }),
      take(1),
      mergeMap((loggedUser: any) => {
        userHash = loggedUser.hash;
        return this.getSolaceDataTopics(loggedUser.hasLevel2);
      }),
    );

    return solaceTopics$.pipe(
      catchError((error: any) => {
        this.notificationService.showServiceError(error);

        return observableThrowError(error);
      }),
      tap((result: any[]) => {
        const topics: SolacePropertie[] = [];

        for (const item of result) {
          const dataServiceAgent: string = item.hasOwnProperty(
            'dataServiceAgent',
          )
            ? item.dataServiceAgent
            : item.serviceAgent;

          const aggServiceAgent: string = item.hasOwnProperty('aggServiceAgent')
            ? item.aggServiceAgent
            : undefined;

          const dataSubscription: string = item.hasOwnProperty(
            'dataSubscription',
          )
            ? item.dataSubscription[0]
            : item.suscription[0];

          const aggSubscription: string = item.hasOwnProperty(
            'dataSubscription',
          )
            ? item.aggSubscription[0]
            : undefined;

          topics.push(
            new SolacePropertie(dataServiceAgent, dataSubscription, userHash),
          );

          if (aggServiceAgent && aggSubscription) {
            topics.push(
              new SolacePropertie(aggServiceAgent, aggSubscription, userHash),
            );
          }
        }

        topics.push(
          new SolacePropertie(
            'svc/GBM/R/MD',
            'dat/GBM/R/L1/MEX/E/BMV/NAC/*',
            userHash,
          ),
        );
        topics.push(
          new SolacePropertie(
            'svc/GBM/R/MD',
            'dat/GBM/R/L1/MEX/E/BMV/SIC/*',
            userHash,
          ),
        );

        this.store.dispatch({
          type: SOLACE_TOPICS,
          payload: topics,
        });

        this.solaceTopics = this.duplicateTopic(topics);
      }),
    );
  }

  private duplicateTopic(topics: SolacePropertie[]): any[] {
    const flags: any = [];
    const uniqueTopics = [];
    const topicsLength = topics !== undefined ? topics.length : 0;

    if (topics === undefined) {
      return topics;
    }

    for (let i = 0; i < topicsLength; i++) {
      if (flags[topics[i].subscriptionTopic]) {
        continue;
      }
      flags[topics[i].subscriptionTopic] = true;
      uniqueTopics.push(topics[i]);
    }
    return uniqueTopics;
  }

  private getSolaceDataTopics(hasLevel2: boolean): Observable<any> {
    const nac$: Observable<any> = this.appManagementRestService
      .getSolaceDataTopic(false)
      .pipe(
        catchError((error: any) => {
          this.notificationService.showServiceError(error);

          return observableThrowError(error);
        }),
        take(1),
      );

    const sic$: Observable<any> = this.appManagementRestService
      .getSolaceDataTopic(true)
      .pipe(
        catchError((error: any) => {
          this.notificationService.showServiceError(error);

          return observableThrowError(error);
        }),
        take(1),
      );

    const indexes$: Observable<any> = this.appManagementRestService
      .getSolaceIndexesTopic()
      .pipe(
        catchError((error: any) => {
          this.notificationService.showServiceError(error);

          return observableThrowError(error);
        }),
        take(1),
      );

    const nacL$: Observable<any> = this.appManagementRestService
      .getSolaceLTopic(false, hasLevel2)
      .pipe(
        catchError((error: any) => {
          this.notificationService.showServiceError(error);

          return observableThrowError(error);
        }),
        take(1),
      );

    const sicL$: Observable<any> = this.appManagementRestService
      .getSolaceLTopic(true, hasLevel2)
      .pipe(
        catchError((error: any) => {
          this.notificationService.showServiceError(error);

          return observableThrowError(error);
        }),
        take(1),
      );

    return observableMerge(nac$, sic$, indexes$, nacL$, sicL$).pipe(toArray());
  }

  private issuesToSet(marketData: MarketViewModel[]): void {
    for (const item of marketData) {
      // TODO: we shouldn't check for duplicated items, but instead,
      // prevent that they are received in this function.
      if (
        !this.searchService.issues.some(
          (e) => e.issueID === item.securityId.replace(/&/g, 'Ñ'),
        )
      ) {
        // If you change the push to an assigmnent to issues (this.searchService.issues = [...]),
        // consider that some components and services refer to this as a **reference**,
        // so if you reassign it, those references will be lost, and won't be updated.
        this.searchService.issues.push({
          instrumentType: item.instrumentType,
          issueID: item.securityId.replace(/&/g, 'Ñ'),
          issueName: new TitleCasePipe().transform(item.issuer),
          issue: `${item.securityId} - ${item.issuer}`,
          weightedAveragePrice: item.weightedAveragePrice,
          instrumentId: item.instrumentId,
          lastPrice: item.lastPrice,
          status: item.status,
          ticker: item.securityId.split(' ')[0],
        });
      }
    }

    // This will leave better matches first when searching for an issue
    this.searchService.issues.sort(
      (a, b) =>
        // Sort first by the length of the ticker,
        a.issueID.length - b.issueID.length ||
        // And if they are the same (the difference is 0), sort alphabetically
        a.issueID.localeCompare(b.issueID),
    );

    marketData[0].instrumentType === MarketType.USA &&
      this.store.dispatch({
        type: USA_MARKET_INITIALIZER,
        payload: true,
      });
  }

  private cleanSearchService() {
    this.searchService.issues.length = 0;
  }
}
